// A task's status as its pill (S141): "Waiting for your answer · 2" or "Waiting for your
// approval", with how long ago it was asked, when the task waits on its owner ("Waiting for VPN
// sign-in" when its environment's VPN is down, S153); the plain
// status otherwise. The step behind a question keeps the task `running` on the wire. A parent
// running its subtasks says "Orchestrating · …" (D87), a held subtask "Waiting for <parent>"
// (linked), and a paused task carries a "Paused" pill beside its status.
import { Hand, Pause, ShieldWarning, TreeStructure } from "@phosphor-icons/react";
import { Link } from "react-router";

import { useT } from "../i18n";
import { formatAgo } from "../i18n/format";
import { Pill, StatusPill } from "../ui/status";
import { TASK_TONE } from "./display";
import type { Task } from "./model";
import type { WaitingOn } from "./subtasks/api";
import { orchestratingLabel } from "./subtasks/waves";

type PillTask = Pick<Task, "status" | "waiting_on"> & Partial<Pick<Task, "paused">>;

function WaitPill({ waiting }: { waiting: WaitingOn }) {
  const { tasks } = useT();
  const t = tasks.waiting;
  switch (waiting.kind) {
    case "questions":
    case "approval": {
      const ago = formatAgo(waiting.since);
      return (
        <span title={t.since(ago)} data-testid="task-waiting">
          <Pill tone="orange" icon={<Hand weight="fill" aria-hidden />}>
            {waiting.kind === "questions" ? t.answer(waiting.count) : t.approval}
            <span className="font-normal opacity-75">· {ago}</span>
          </Pill>
        </span>
      );
    }
    case "vpn": {
      const ago = formatAgo(waiting.since);
      return (
        <span title={t.vpnSince(ago)} data-testid="task-waiting-vpn">
          <Pill tone="orange" icon={<ShieldWarning weight="fill" aria-hidden />}>
            {t.vpn}
            <span className="font-normal opacity-75">· {ago}</span>
          </Pill>
        </span>
      );
    }
    case "subtasks": {
      const needsYou = waiting.waiting > 0;
      return (
        <span data-testid="task-orchestrating">
          <Pill tone={needsYou ? "orange" : waiting.failed > 0 ? "red" : "blue"} icon={<TreeStructure weight="bold" aria-hidden />}>
            {orchestratingLabel(waiting, tasks.subtasks)}
          </Pill>
        </span>
      );
    }
    case "parent":
      return (
        <span data-testid="task-held">
          <Pill tone="gray" icon={<TreeStructure weight="bold" aria-hidden />}>
            <Link to={`/tasks/${waiting.task_id}`} className="text-inherit hover:underline">
              {tasks.subtasks.waitingForParent(waiting.ref)}
            </Link>
          </Pill>
        </span>
      );
  }
}

export function TaskStatusPill({ task }: { task: PillTask }) {
  const t = useT().tasks.subtasks;
  const waiting = task.waiting_on;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {waiting ? <WaitPill waiting={waiting} /> : <StatusPill tone={TASK_TONE[task.status]} status={task.status} />}
      {task.paused ? (
        <span data-testid="task-paused">
          <Pill tone="gray" icon={<Pause weight="fill" aria-hidden />}>
            {t.paused}
          </Pill>
        </span>
      ) : null}
    </span>
  );
}
